import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { DataPreview, buildDataPreview } from './data-preview';
import { PystRequestError, PystService, EMBED_TOKEN_LONGEST_TTL_SECONDS } from './pyst.service';

/**
 * The reset pyst injects into its plot HTML (py_plot_fit_embed), used here only as a
 * marker to recognise a document that already fits its frame.
 */
const PYST_EMBED_RESET = 'html,body{margin:0;padding:0;height:100%;width:100%;overflow:hidden}';

/**
 * Fallback for a pyst that predates the embed fix. Its plot document has the
 * browser's 8px body margin and no height on the plot div's ancestors, so plotly
 * falls back to 450px and the frame shows scrollbars. This is a viewer convenience
 * only: the served document is what other embedders get, so the real fix lives in
 * pyst and this reports when it had to step in.
 */
function fitEmbedIfNeeded(html: string): { html: string; fitted: boolean } {
    if (html.includes(PYST_EMBED_RESET)) {
        return { html, fitted: false };
    }
    const style = `<style>${PYST_EMBED_RESET}body>div{height:100%;width:100%}</style>`;
    const head = html.indexOf('</head>');
    const patched = head === -1 ? style + html : html.slice(0, head) + style + html.slice(head);
    return { html: patched, fitted: true };
}

export interface QueryRunStatus {
    /** Short line for a caller's own header: size, duration, or the failure. */
    text: string;
    failed: boolean;
    /** The document needed the viewer's embed fit, so pyst is behind. */
    fitted: boolean;
}

/**
 * What one pyst query returned: a plot, a table, or the error it produced.
 *
 * Shared by the plot card and the editor's Test button, so running a query means
 * the same thing in both and a rendering bug can only exist in one place. The
 * caller owns when to run and what to show around it.
 */
@Component({
    selector: 'app-query-result',
    template: `
        <iframe
            *ngIf="frameUrl && !preview"
            [src]="frameUrl"
            title="Query result"
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        ></iframe>
        <app-data-preview *ngIf="preview" [preview]="preview" [bytes]="previewBytes"></app-data-preview>
    `,
    styles: [
        `
            :host {
                display: block;
                position: relative;
                width: 100%;
                height: 100%;
                overflow: hidden;
                background: #fff;
            }

            iframe {
                width: 100%;
                height: 100%;
                border: 0;
                display: block;
            }

            /*
             * Size only. Setting display here would override the child's own
             * :host rule, since a parent's element selector wins over it, which is
             * how the table lost its layout and collapsed to a minimum height.
             */
            app-data-preview {
                width: 100%;
                height: 100%;
            }
        `,
    ],
})
export class QueryResultComponent implements OnDestroy {
    /** The query to run when `run()` is called. */
    @Input() query = '';
    @Output() statusChange = new EventEmitter<QueryRunStatus>();

    public frameUrl: SafeResourceUrl | null = null;
    public preview: DataPreview | null = null;
    public previewBytes = 0;
    public busy = false;
    public failed = false;
    public viewerFitted = false;
    /** Why the last mint failed, for the button that asked for it. */
    public embedError: string | null = null;
    /** When the last minted link stops working (the server's maximum lifetime). */
    public embedExpiresAt: string | null = null;

    private objectUrl: string | null = null;

    constructor(
        private pyst: PystService,
        private sanitizer: DomSanitizer,
    ) {}

    ngOnDestroy(): void {
        this.releaseObjectUrl();
    }

    /** Whether anything has been shown yet. */
    get empty(): boolean {
        return !this.frameUrl && !this.preview;
    }

    async run(): Promise<void> {
        if (this.busy) {
            return;
        }
        const query = this.query;
        if (!query.trim()) {
            return;
        }
        this.busy = true;
        this.emit('running…', false, false);
        const startedAt = Date.now();

        try {
            const { text, contentType } = await this.pyst.getRaw(query);
            const seconds = Math.round((Date.now() - startedAt) / 100) / 10;
            if (contentType.includes('html')) {
                this.preview = null;
                const { html, fitted } = fitEmbedIfNeeded(text);
                this.viewerFitted = fitted;
                this.show(html);
                this.failed = false;
                this.emit(
                    `${formatBytes(text.length)} · ${seconds}s${fitted ? ' · viewer-fitted' : ''}`,
                    false,
                    fitted,
                );
            } else {
                // Not a plot: build a table or a sample without parsing more of it
                // than is safe, and let go of any blob the previous run held.
                this.releaseObjectUrl();
                this.frameUrl = null;
                this.viewerFitted = false;
                this.previewBytes = text.length;
                this.preview = buildDataPreview(text);
                this.failed = false;
                this.emit(`${formatBytes(text.length)} · ${seconds}s · data`, false, false);
            }
        } catch (error) {
            const failure = error as PystRequestError;
            const status = failure?.status
                ? `HTTP ${failure.status} ${failure.statusText ?? ''}`.trim()
                : failure?.message || 'Request failed';
            // Show the URL actually requested: a mangled path is the failure mode
            // that is otherwise invisible from inside the frame.
            const url = failure?.url || this.pyst.url(query);
            const detail = failure?.body?.trim() ? failure.body.slice(0, 8000) : 'no response body';
            this.preview = null;
            this.show(errorPage(status, `${url}\n\n${detail}`));
            this.failed = true;
            this.emit(status, true, false);
        } finally {
            this.busy = false;
        }
    }

    /**
     * Open this query's own URL in a new tab, carrying a freshly minted embed
     * token: the query itself, not a copy of its output, which is what another
     * application would put in an iframe. The tab is opened before the await or
     * the browser treats it as a popup.
     *
     * The token is bound to this exact query and carries read scope only, so it
     * runs that one plot until it expires and nothing else. It lives as long as
     * the server allows (PYST_EMBED_TOKEN_MAX_TTL_SECONDS).
     */
    async openInNewTab(): Promise<void> {
        const query = this.query;
        if (!query.trim()) {
            return;
        }
        const tab = window.open('about:blank', '_blank');
        try {
            const minted = await this.pyst.mintEmbedToken(query, EMBED_TOKEN_LONGEST_TTL_SECONDS);
            this.embedExpiresAt = minted.expiresAt ?? null;
            if (tab) {
                tab.location.href = minted.url;
            } else {
                window.open(minted.url, '_blank');
            }
            this.embedError = null;
        } catch (error) {
            tab?.close();
            const failure = error as PystRequestError;
            this.embedError = failure?.status
                ? `HTTP ${failure.status} ${failure.statusText ?? ''}`.trim()
                : (error as Error)?.message || 'request failed';
            console.warn('Could not mint an embed token', error);
        }
    }

    private emit(text: string, failed: boolean, fitted: boolean): void {
        this.statusChange.emit({ text, failed, fitted });
    }

    private show(html: string): void {
        this.releaseObjectUrl();
        this.objectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        this.frameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    }

    private releaseObjectUrl(): void {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }
    }
}

export function formatBytes(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(0)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function errorPage(title: string, detail: string): string {
    const esc = (value: string) =>
        value.replace(/[&<>]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[char] as string));
    return `<!doctype html><meta charset="utf-8">
<style>
  body { font: 13px ui-monospace, SFMono-Regular, Menlo, monospace; margin: 0; padding: 16px;
         background: #1b1416; color: #ffc9c9; }
  h1 { font-size: 14px; margin: 0 0 12px; color: #ff8787; }
  pre { white-space: pre-wrap; word-break: break-word; margin: 0; opacity: .85; }
</style>
<h1>${esc(title)}</h1><pre>${esc(detail)}</pre>`;
}
